import { fmtIDR } from "@/lib/constants";

export const BANK_DETAILS = { bank: "BCA", account_number: "3830880797", account_holder: "Fenny" };
export const INSTAGRAM_HANDLE = "@heelsandtea.id";

/** Numbered payment history (Version D): DP 1, DP 2… in chronological order; settlements keep their label. */
export const paymentHistoryRows = (payments = []) => {
  const sorted = [...payments].sort((a, b) => String(a.paid_at || "").localeCompare(String(b.paid_at || "")));
  let dp = 0;
  return sorted.map((p) => {
    const settlement = (p.payment_type || "").toUpperCase() === "SETTLEMENT";
    if (!settlement) dp += 1;
    return {
      id: p.id,
      label: settlement ? "FULL SETTLEMENT" : `DP ${dp}`,
      method: p.method || "",
      paid_at: p.paid_at || "",
      amount: p.amount,
    };
  });
};
const LOGO_URL = "/brand/heels-and-tea-logo.jpg";
const ROSE = [183, 110, 121];
const INK = [23, 23, 23];
const MUTED = [115, 115, 115];
const LINE = [228, 228, 228];

const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0);
      try {
        resolve({ data: c.toDataURL("image/jpeg", 0.92), ratio: img.naturalWidth / img.naturalHeight });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = LOGO_URL;
  });

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export const invoiceFileName = (invoice) =>
  `Invoice-${invoice.invoice_number || invoice.id.slice(0, 8)}-${invoice.customer_name.replace(/[^a-z0-9]+/gi, "-")}.pdf`;

/** Builds the customer-facing A4 invoice with native jsPDF text (no html2canvas -> works on Android). */
export async function buildInvoicePdf(invoice) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 18;
  let y = 16;

  const logo = await loadLogo();
  if (logo) {
    const lw = 34;
    doc.addImage(logo.data, "JPEG", (W - lw) / 2, y, lw, lw / logo.ratio, undefined, "FAST");
    y += lw / logo.ratio + 7;
  } else {
    doc.setFont("helvetica", "bold").setFontSize(20).setTextColor(...INK);
    doc.text("HEELS & TEA", W / 2, y + 8, { align: "center" });
    y += 18;
  }

  doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(...ROSE);
  doc.text("F I N A L   I N V O I C E", W / 2, y, { align: "center" });
  y += 7;
  doc.setDrawColor(...LINE).setLineWidth(0.2).line(M, y, W - M, y);
  y += 9;

  // Billed to / invoice meta
  doc.setFont("helvetica", "bold").setFontSize(7).setTextColor(...MUTED);
  doc.text("CUSTOMER", M, y);
  doc.text("INVOICE NO.", W - M, y, { align: "right" });
  y += 5;
  doc.setFont("helvetica", "bold").setFontSize(12).setTextColor(...INK);
  doc.text(invoice.customer_name, M, y);
  doc.setFontSize(10);
  doc.text(invoice.invoice_number || `HT-${invoice.id.slice(0, 8).toUpperCase()}`, W - M, y, { align: "right" });
  y += 5;
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(...MUTED);
  const contact = [invoice.customer_phone, invoice.customer_instagram ? `@${invoice.customer_instagram}` : null].filter(Boolean);
  if (contact.length) doc.text(contact.join("  ·  "), M, y);
  doc.text(fmtDate(invoice.created_at), W - M, y, { align: "right" });
  y += 11;

  // Items table
  const colQty = W - M - 74;
  const colUnit = W - M - 38;
  doc.setFont("helvetica", "bold").setFontSize(7).setTextColor(...MUTED);
  doc.text("PRODUCT", M, y);
  doc.text("QTY", colQty, y, { align: "right" });
  doc.text("UNIT PRICE", colUnit, y, { align: "right" });
  doc.text("SUBTOTAL", W - M, y, { align: "right" });
  y += 2.5;
  doc.setDrawColor(...LINE).line(M, y, W - M, y);
  y += 5.5;

  doc.setFontSize(9.5).setTextColor(...INK);
  (invoice.lines || []).forEach((l) => {
    if (y > H - 70) {
      doc.addPage();
      y = 20;
    }
    const name = doc.splitTextToSize(l.product_name + (l.variant ? ` · ${l.variant}` : ""), colQty - M - 6);
    doc.setFont("helvetica", "normal");
    doc.text(name, M, y);
    doc.text(String(l.quantity), colQty, y, { align: "right" });
    doc.setTextColor(...MUTED);
    doc.text(l.selling_price != null ? fmtIDR(l.selling_price) : "—", colUnit, y, { align: "right" });
    doc.setFont("helvetica", "bold").setTextColor(...INK);
    doc.text(l.selling_price != null ? fmtIDR(l.line_total) : "—", W - M, y, { align: "right" });
    y += Math.max(name.length * 4.6, 4.6) + 3.4;
    doc.setDrawColor(245, 245, 245).line(M, y - 1.8, W - M, y - 1.8);
  });

  // Totals
  y += 4;
  const labelX = W - M - 52;
  const row = (label, value, opts = {}) => {
    doc.setFont("helvetica", opts.strong ? "bold" : "normal").setFontSize(opts.strong ? 10 : 9.5);
    doc.setTextColor(...(opts.strong ? INK : MUTED));
    doc.text(label, labelX, y, { align: "right" });
    doc.setFont("helvetica", "bold").setTextColor(...INK);
    doc.text(value, W - M, y, { align: "right" });
    y += opts.gap || 5.6;
  };
  row("SUBTOTAL", fmtIDR(invoice.subtotal));
  row("ONGKOS KIRIM", fmtIDR(invoice.shipping_charge));
  if (invoice.insurance_amount > 0) row("Insurance", fmtIDR(invoice.insurance_amount));
  if (invoice.adjustment) row("Adjustment", fmtIDR(invoice.adjustment));
  doc.setDrawColor(...LINE).line(labelX - 22, y - 2.4, W - M, y - 2.4);
  y += 1.6;
  row("TOTAL", fmtIDR(invoice.total), { strong: true });
  row("SUDAH DIBAYAR", fmtIDR(invoice.paid_verified));
  if (invoice.credit_used > 0) row("CUSTOMER CREDIT", fmtIDR(invoice.credit_used));
  doc.setDrawColor(...LINE).line(labelX - 22, y - 2.4, W - M, y - 2.4);
  y += 2.5;
  doc.setFont("helvetica", "bold").setFontSize(10).setTextColor(...INK);
  doc.text("SISA TAGIHAN", labelX, y, { align: "right" });
  doc.setFontSize(14);
  doc.text(fmtIDR(Math.max(invoice.balance_due, 0)), W - M, y, { align: "right" });
  y += 7;

  const isPaid = invoice.balance_due <= 0;
  doc.setFont("helvetica", "bold").setFontSize(8).setTextColor(...(isPaid ? ROSE : INK));
  doc.text(isPaid ? "P A I D" : "B A L A N C E   D U E", W - M, y, { align: "right" });
  y += 10;

  // RIWAYAT DP first, then the permanent bank details box
  const payments = invoice.payments || [];
  if (payments.length) {
    if (y > H - 40) {
      doc.addPage();
      y = 20;
    }
    doc.setFont("helvetica", "bold").setFontSize(7).setTextColor(...MUTED);
    doc.text("RIWAYAT DP", M, y);
    y += 5;
    doc.setFont("helvetica", "normal").setFontSize(9);
    paymentHistoryRows(payments).forEach((p) => {
      doc.setTextColor(...MUTED);
      doc.text(`${p.label}  ·  ${p.method}  ·  ${p.paid_at}`, M, y);
      doc.setFont("helvetica", "bold").setTextColor(...INK);
      doc.text(fmtIDR(p.amount), W - M, y, { align: "right" });
      doc.setFont("helvetica", "normal");
      y += 5.2;
    });
    y += 4;
  }

  if (y > H - 58) {
    doc.addPage();
    y = 20;
  }
  const cx = W / 2;
  doc.setFillColor(242, 246, 253).setDrawColor(195, 212, 239);
  doc.roundedRect(M, y, W - M * 2, 26, 2, 2, "FD");
  doc.setFont("helvetica", "bold").setFontSize(7).setTextColor(45, 92, 168);
  doc.text("PAYMENT DETAILS", cx, y + 7, { align: "center" });
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(74, 111, 165);
  doc.text(`BANK ${BANK_DETAILS.bank}  ·  A/N ${BANK_DETAILS.account_holder.toUpperCase()}`, cx, y + 14, { align: "center" });
  doc.setFont("helvetica", "bold").setFontSize(15).setTextColor(31, 75, 145);
  doc.text(BANK_DETAILS.account_number, cx, y + 22, { align: "center" });
  y += 34;

  if (invoice.notes) {
    doc.setFont("helvetica", "italic").setFontSize(9).setTextColor(...MUTED);
    doc.text(doc.splitTextToSize(invoice.notes, W - M * 2), M, y);
  }

  // Footer on every page
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setDrawColor(...LINE).line(M, H - 24, W - M, H - 24);
    doc.setFont("helvetica", "bold").setFontSize(8).setTextColor(...ROSE);
    doc.text("THANK YOU FOR SHOPPING WITH US", W / 2, H - 17, { align: "center" });
    doc.setFont("helvetica", "normal").setFontSize(8.5).setTextColor(...MUTED);
    doc.text(INSTAGRAM_HANDLE, W / 2, H - 11, { align: "center" });
  }
  return doc;
}
